import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import '../../App.css';

interface WorkspaceSelectorProps {
  workspaces: Array<{ id: string; slug: string; name: string; workspaceRole?: string }>;
  user: { id: string; email: string; isPortalAdmin?: boolean };
  onWorkspaceSelected: () => void;
}

const WorkspaceSelector: React.FC<WorkspaceSelectorProps> = ({
  workspaces,
  user,
  onWorkspaceSelected
}) => {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [workspaceSelected, setWorkspaceSelected] = useState(false);
  const { selectWorkspace, isAuthenticated, user: authUser } = useAuth();
  const navigate = useNavigate();

  // After workspace is selected and user is authenticated, navigate
  useEffect(() => {
    if (workspaceSelected && isAuthenticated && authUser) {
      setLoading(false);
      onWorkspaceSelected();
      navigate('/');
    }
  }, [workspaceSelected, isAuthenticated, authUser, onWorkspaceSelected, navigate]);

  const handleSelect = async () => {
    if (!selectedWorkspaceId) {
      setError('Please select a workspace');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await selectWorkspace(selectedWorkspaceId);
      // Mark workspace as selected - useEffect will handle navigation when auth state updates
      setWorkspaceSelected(true);
    } catch (err: any) {
      setError(err.message || 'Failed to select workspace');
      setLoading(false);
    }
  };

  return (
    <div className="form-container">
      <div className="card" style={{ maxWidth: '500px', margin: '2rem auto' }}>
        <h2 className="page-title">Select Workspace</h2>
        <p className="subtitle">
          You have access to multiple workspaces. Please select one to continue.
        </p>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'var(--error-bg)', color: 'var(--error-text)', borderRadius: '4px' }}>
            {error}
          </div>
        )}

        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>Workspace</label>
          <select
            value={selectedWorkspaceId}
            onChange={(e) => setSelectedWorkspaceId(e.target.value)}
            className="form-input"
            disabled={loading}
            style={{ width: '100%', padding: '0.75rem' }}
          >
            <option value="">Select a workspace...</option>
            {workspaces.map(workspace => (
              <option key={workspace.id} value={workspace.id}>
                {workspace.name} ({workspace.slug})
                {workspace.workspaceRole && ` - ${workspace.workspaceRole}`}
              </option>
            ))}
          </select>
        </div>

        <button
          onClick={handleSelect}
          disabled={!selectedWorkspaceId || loading}
          className="btn btn-primary"
          style={{ width: '100%', marginTop: '1rem' }}
        >
          {loading ? 'Loading...' : 'Continue'}
        </button>
      </div>
    </div>
  );
};

export default WorkspaceSelector;

